import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, TrendingUp, Target, Lightbulb, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingRecommendationPanel({ contractId, currentRate }) {
  const [recommendations, setRecommendations] = useState(null);

  const recommendMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/${contractId}/pricing-recommendation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setRecommendations(data.recommendations);
      toast.success('Pricing recommendations generated!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to generate recommendations');
    },
  });

  const formatRate = (rate) => `$${rate.toFixed(4)}/kWh`;
  const formatPercent = (pct) => {
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
  };

  const getChangeColor = (pct) => {
    if (pct < -5) return 'text-green-700 bg-green-100';
    if (pct < 0) return 'text-blue-700 bg-blue-100';
    if (pct < 5) return 'text-yellow-700 bg-yellow-100';
    return 'text-orange-700 bg-orange-100';
  };

  const renderScenario = (scenario, isPrimary = false) => (
    <Card className={isPrimary ? 'border-2 border-[#09409C]' : ''} data-testid={`scenario-${scenario.name}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl flex items-center gap-2">
            {scenario.name}
            {isPrimary && <Badge className="bg-[#09409C]">Recommended</Badge>}
          </CardTitle>
          <Badge className="text-lg px-3 py-1">{formatRate(scenario.proposedRate)}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Rate Change</p>
            <Badge className={`mt-1 ${getChangeColor(scenario.rateChangePct)}`}>
              {formatPercent(scenario.rateChangePct)}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Retention Probability</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${scenario.retentionProbability >= 70 ? 'bg-green-500' : scenario.retentionProbability >= 50 ? 'bg-yellow-500' : 'bg-orange-500'}`}
                  style={{ width: `${scenario.retentionProbability}%` }}
                />
              </div>
              <span className="text-sm font-semibold">{scenario.retentionProbability}%</span>
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm text-muted-foreground">Estimated Margin</p>
          <p className="text-2xl font-bold text-[#229644]">{scenario.estimatedMargin.toFixed(1)}%</p>
        </div>

        {scenario.termOptions && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">Term Options</p>
            <div className="flex gap-2">
              {scenario.termOptions.map((term) => (
                <Badge key={term} variant="outline">
                  {term} months
                </Badge>
              ))}
            </div>
          </div>
        )}

        {scenario.pros && (
          <div>
            <p className="text-sm font-semibold mb-2">Pros</p>
            <ul className="space-y-1">
              {scenario.pros.map((pro, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                  {pro}
                </li>
              ))}
            </ul>
          </div>
        )}

        {scenario.cons && (
          <div>
            <p className="text-sm font-semibold mb-2">Cons</p>
            <ul className="space-y-1">
              {scenario.cons.map((con, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                  {con}
                </li>
              ))}
            </ul>
          </div>
        )}

        {scenario.rationale && (
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-sm text-gray-700">{scenario.rationale}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Card className="border-2 border-[#229644]" data-testid="pricing-recommendation-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Target className="w-6 h-6 text-[#229644]" />
              AI Pricing Recommendations
            </CardTitle>
            <CardDescription className="mt-2">
              Optimized renewal pricing strategies powered by AI
            </CardDescription>
          </div>
          <Button
            onClick={() => recommendMutation.mutate()}
            disabled={recommendMutation.isPending}
            className="bg-gradient-to-r from-[#229644] to-[#1a7533]"
            data-testid="generate-pricing-button"
          >
            {recommendMutation.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <DollarSign className="w-4 h-4 mr-2" />
                Generate Recommendations
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      {recommendations && (
        <CardContent className="space-y-6">
          {/* Current Context */}
          <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-gray-50 border">
            <div>
              <p className="text-sm text-muted-foreground">Current Rate</p>
              <p className="text-2xl font-bold">{formatRate(recommendations.currentRate)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Market Rate</p>
              <p className="text-2xl font-bold text-[#146CBB]">{formatRate(recommendations.basedOnMarketRate)}</p>
            </div>
          </div>

          {/* Primary Recommendation */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#09409C]" />
              Primary Recommendation
            </h3>
            {renderScenario(recommendations.primaryRecommendation, true)}
          </div>

          {/* Alternative Scenarios */}
          {recommendations.scenarios && recommendations.scenarios.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Alternative Scenarios</h3>
              <Tabs defaultValue="0" className="w-full">
                <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${recommendations.scenarios.length}, 1fr)` }}>
                  {recommendations.scenarios.map((scenario, idx) => (
                    <TabsTrigger key={idx} value={String(idx)}>
                      {scenario.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {recommendations.scenarios.map((scenario, idx) => (
                  <TabsContent key={idx} value={String(idx)}>
                    {renderScenario(scenario)}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          )}

          {/* Key Considerations */}
          {recommendations.keyConsiderations && (
            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Key Considerations
              </h4>
              <ul className="space-y-1">
                {recommendations.keyConsiderations.map((consideration, idx) => (
                  <li key={idx} className="text-sm text-gray-700 ml-4 list-disc">
                    {consideration}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Competitive Intelligence */}
          {recommendations.competitiveIntelligence && (
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
              <h4 className="font-semibold mb-2">Market Intelligence</h4>
              <p className="text-sm text-gray-700">{recommendations.competitiveIntelligence}</p>
            </div>
          )}

          {/* Suggested Talking Points */}
          {recommendations.suggestedTalkingPoints && (
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <h4 className="font-semibold mb-2">Suggested Talking Points for Customer</h4>
              <ul className="space-y-1">
                {recommendations.suggestedTalkingPoints.map((point, idx) => (
                  <li key={idx} className="text-sm text-gray-700 ml-4 list-disc">
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Metadata */}
          <div className="text-xs text-muted-foreground text-center pt-4 border-t">
            Recommendations generated at {new Date(recommendations.generatedAt).toLocaleString()}
          </div>
        </CardContent>
      )}

      {!recommendations && !recommendMutation.isPending && (
        <CardContent>
          <div className="text-center py-12 text-muted-foreground">
            <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">No recommendations available yet</p>
            <p className="text-sm">Click "Generate Recommendations" to get AI-powered pricing strategies</p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
